import React from "react";
import { ArrowRight, Search, Server, Rocket } from "lucide-react";

export default function OnboardingSection() {
  const scrollToContact = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
    setTimeout(() => {
      const target = document.querySelector("#intake-form");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const phases = [
    {
      step: "Phase 1",
      duration: "Days 1–15",
      title: "Capital & Architectural Audit",
      description: "Evaluate existing stack, AI budget utilization ($500K–$20M zone), and data pipeline security.",
      icon: <Search className="w-5 h-5 text-brand-orange" />
    },
    {
      step: "Phase 2",
      duration: "Days 16–45",
      title: "Core Architecture & Governance",
      description: "Implement Neo4j/PostgreSQL foundations, establish Rust/Python standards, and construct semantic validation layers.",
      icon: <Server className="w-5 h-5 text-brand-orange" />
    },
    {
      step: "Phase 3",
      duration: "Days 46–90",
      title: "Production Deployment & Scale",
      description: "Deploy deterministic AI workflows, lock down audit logging, and train technical teams on operational boundaries.",
      icon: <Rocket className="w-5 h-5 text-brand-orange" />
    }
  ];

  return (
    <section id="onboarding" className="max-w-6xl mx-auto px-6 py-24 md:py-28 relative overflow-hidden">
      
      {/* Header Block */}
      <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="h-px bg-brand-sand grow max-w-[40px]" />
          <span className="text-xs font-mono uppercase tracking-widest text-brand-burgundy font-bold">
            04. OPERATIONAL ENGAGEMENT FRAMEWORK
          </span>
          <div className="h-px bg-brand-sand grow max-w-[40px]" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-charcoal font-display">
          90-Day Execution Timeline
        </h2>
        <p className="text-sm text-stone-700 font-sans max-w-2xl mx-auto leading-relaxed mt-4 font-light">
          A structured framework to move from capital assessment to deterministic production scale, ensuring measurable governance within the first quarter of engagement.
        </p>
      </div>

      {/* Timeline Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto mb-16">
        {phases.map((item, idx) => (
          <div 
            key={idx}
            className="bg-white border border-brand-sand rounded-sm p-8 flex flex-col justify-between shadow-sm hover:shadow-md hover:border-brand-burgundy/50 transition-all duration-300 relative group"
          >
            {/* Top row with step and duration */}
            <div className="flex justify-between items-center mb-6 border-b border-brand-sand/50 pb-4">
              <span className="text-[10px] font-mono font-bold tracking-widest text-brand-burgundy bg-brand-burgundy/5 px-2.5 py-1 rounded-sm border border-brand-burgundy/10">
                {item.step}
              </span>
              <span className="text-[9px] font-mono font-bold tracking-wider text-stone-500 uppercase flex items-center gap-1.5">
                {item.icon} {item.duration}
              </span>
            </div>

            {/* Content */}
            <div className="flex-grow space-y-4">
              <h3 className="text-xl font-display font-bold text-brand-charcoal tracking-tight group-hover:text-brand-burgundy transition-colors">
                {item.title}
              </h3>
              <p className="text-sm text-stone-600 font-sans font-light leading-relaxed border-l-2 border-brand-orange/30 pl-3">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Onboarding CTA Section */}
      <div className="mt-12 text-center">
        <h4 className="text-xs font-mono font-bold uppercase tracking-widest text-brand-charcoal mb-4">
          Ready to begin?
        </h4>
        <button
          onClick={scrollToContact}
          className="w-full sm:w-auto bg-brand-burgundy hover:bg-brand-orange text-white font-mono font-bold uppercase tracking-wider text-xs py-3.5 px-6 rounded-sm shadow-md transition-colors text-center cursor-pointer inline-flex items-center justify-center gap-1.5"
        >
          Initiate Intake <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

    </section>
  );
}
